/**
 * Feature flag service.
 *
 * Flags are consumed synchronously via {@link FeatureFlagService.isEnabled} so
 * components can call them in render. Values come from three sources, merged
 * in the following precedence (later beats earlier):
 *
 * 1. Hardcoded defaults in the class below (compile-time safety net).
 * 2. `window.featureFlags` injected by the CDK deployment construct into
 *    `config.js` at deploy time (legacy fallback).
 * 3. `/api/config/feature-flags` fetched at app boot in `main.tsx`
 *    (runtime source of truth — the admin panel writes here).
 *
 * The consumer API (`isEnabled`, `updateFlags`, `getAllFlags`) is unchanged;
 * only the flag source pipeline has grown.
 */

export interface FeatureFlags {
  enableStripePayments: boolean;
  requirePremiumSubscription: boolean;
  enableAdvancedSearch: boolean;
  enableImageProcessing: boolean;
  enableGeneralCatalog: boolean;
  enableSmsMfa: boolean;
  /**
   * When on, exposes the redesigned /search page (faceted filter rail,
   * cross-scope search). Off in prod until the design + cross-catalog
   * indexer work has soaked in test. See
   * ~/.claude/plans/fetch-this-design-file-cryptic-corbato.md.
   */
  enableSearchPageRedesign: boolean;
  /**
   * When on, exposes the My Objects "Bulk Edit" action: select multiple objects
   * and apply shared metadata (creation year, Getty work type, materials,
   * techniques, substrate, keywords) to all of them at once. Off in prod until
   * validated on test (test enables it via FEATURE_FLAG_DEFAULTS).
   */
  enableBulkEdit: boolean;
  /**
   * When on, exposes the redesigned /edit-object page (10-step linear wizard
   * with optional-step grouping, dedicated Getty Vocab step, and per-section
   * visibility controls). Off in prod until the new flow has soaked in test.
   * See ~/.claude/plans/starry-leaping-plum.md.
   */
  enableEditObjectRedesign: boolean;
  /**
   * When on, replaces the existing Settings → Subscription "isPaying" view
   * (a tier chip + "Manage Billing" link) with the in-app subscription
   * management page: in-app billing-period switching, cancellation,
   * additional-catalog add-ons, and discount-code entry. Off in prod until
   * PR 2 (backend) and PR 3 (wire-up) land. PR 1 ships the UI against an
   * in-memory mock so the design can be reviewed visually behind this flag.
   * See ~/.claude/plans/claude-code-prompt-temporal-garden.md.
   */
  enableSubscriptionManagement: boolean;
  /**
   * Vestigial flag from the catalog-switcher unification rollout. Runtime
   * gating was removed when the unified switcher became the only code path
   * (the legacy ``CatalogSwitcher`` component is gone). The flag declaration
   * is retained for one release so admin tooling doesn't choke on a missing
   * key, then removed entirely. Toggling this at runtime has no effect.
   */
  enableCatalogSwitching: boolean;
  /**
   * GH#1254 subscription-expiry lifecycle. Gates the expiry-aware UI:
   * the Reactivate button on expired catalog rows, the cancel-prompt
   * "keep your public work visible?" step, the account-deactivated banner,
   * and the admin public-access-policy toggle. On in test; prod flips at
   * MR-8 once the cascade has soaked.
   */
  enableExpiryLifecycle: boolean;
  /**
   * GH#1263/GH#1265: exposes the SearchFilterRail facet sidebar (Getty
   * work type/techniques/materials, creator, creation-year facets) on the
   * public catalog page and My Objects, replacing the client-side (broken,
   * page-only) AdvancedFilter dialog. Off in prod until validated on test.
   */
  enableCatalogFilters: boolean;
}

/**
 * Metadata describing a single feature flag. The admin panel iterates
 * {@link FEATURE_FLAG_REGISTRY} to render toggle controls — adding a new
 * flag only requires adding an entry here (and mirroring it in the backend
 * `DEFAULT_FLAGS` dict + the `FeatureFlags` interface above).
 */
export interface FeatureFlagMetadata {
  key: keyof FeatureFlags;
  /** Human-friendly label shown beside the toggle in the admin panel. */
  label: string;
  /** Long-form explanation rendered under the toggle. */
  description: string;
  /** Fallback value if the boot-time API fetch fails. */
  defaultValue: boolean;
  /**
   * If true, this flag is exposed on the unauthenticated
   * `/api/config/feature-flags` endpoint. Mirror this in the backend
   * `PUBLIC_FLAGS` set so both sides agree on what leaks to anonymous clients.
   */
  public: boolean;
  /** Optional grouping used for section headers in the admin panel. */
  category?: string;
}

/**
 * Single source of truth for all feature flags. The admin panel UI is
 * auto-generated from this list.
 */
export const FEATURE_FLAG_REGISTRY: FeatureFlagMetadata[] = [
  {
    key: 'enableSmsMfa',
    label: 'SMS MFA',
    description:
      'Allow users to register a phone number as a second MFA factor during signup and setup. When off, only authenticator-app TOTP is offered. SMS MFA is vulnerable to SIM-swapping and has per-message cost, so keep off unless there is a specific need.',
    defaultValue: false,
    public: true,
    category: 'Authentication',
  },
  {
    key: 'enableSearchPageRedesign',
    label: 'Search page redesign',
    description:
      'Expose the new /search page with a persistent filter rail, faceted browsing, and cross-scope search. Off in prod until the cross-catalog indexer work has soaked in test.',
    defaultValue: false,
    public: true,
    category: 'Search',
  },
  {
    key: 'enableEditObjectRedesign',
    label: 'Edit object redesign',
    description:
      'Expose the new /edit-object page — a 10-step linear wizard with optional-step grouping, a dedicated Getty Vocab step, and per-section visibility controls. Off in prod until the new flow has soaked in test.',
    defaultValue: false,
    public: true,
    category: 'Catalog',
  },
  {
    key: 'enableBulkEdit',
    label: 'Bulk edit objects',
    description:
      'Expose the My Objects "Bulk Edit" action — select multiple objects and apply shared metadata (creation year, Getty work type, materials, techniques, substrate, keywords) to all of them at once. Off in prod until validated on test.',
    defaultValue: false,
    public: true,
    category: 'Catalog',
  },
  {
    key: 'enableSubscriptionManagement',
    label: 'In-app subscription management',
    description:
      'Replaces the legacy Settings → Subscription view (tier chip + Stripe portal link) with the in-app management page: billing-period switching, cancellation, additional-catalog add-ons, and discount codes. Wired to /api/subscription/* — the page is creator-tier only.',
    defaultValue: true,
    public: true,
    category: 'Billing',
  },
  {
    key: 'enableCatalogSwitching',
    label: 'Multi-catalog switching',
    description:
      'Kill-switch for the unified catalog switcher (owned + delegated catalogs in one header menu). Default ON; will be removed entirely in a follow-up release.',
    defaultValue: true,
    public: true,
    category: 'Catalog',
  },
  {
    key: 'enableExpiryLifecycle',
    label: 'Subscription-expiry lifecycle',
    description:
      'Gates the subscription-expiry cascade UI (GH#1254): Reactivate on expired catalogs, the cancel-prompt public-visibility choice, the account-deactivated banner, and the admin public-access-policy control. Backend cascade is gated by the flag of the same name.',
    defaultValue: false,
    public: true,
    category: 'Billing',
  },
  {
    key: 'enableCatalogFilters',
    label: 'Catalog filter rail',
    description:
      'Server-side facet filters (Getty work type/techniques/materials, creator, creation year) on the public catalog and My Objects, via the same SearchFilterRail used on /search. Off in prod until validated on test.',
    defaultValue: false,
    public: true,
    category: 'Catalog',
  },
];

/** Shape of the JSON returned by `GET /api/config/feature-flags`. */
interface FeatureFlagsApiResponse {
  flags: Partial<FeatureFlags>;
}

const FEATURE_FLAGS_ENDPOINT = '/api/config/feature-flags';

export class FeatureFlagService {
  private flags: FeatureFlags = {
    enableStripePayments: true,
    requirePremiumSubscription: true,
    enableAdvancedSearch: true,
    enableImageProcessing: true,
    enableGeneralCatalog: true,
    enableSmsMfa: false,
    enableSearchPageRedesign: false,
    enableEditObjectRedesign: false,
    // Default OFF (prod-safe); test env flips it on via the public
    // /api/config/feature-flags response (FEATURE_FLAG_DEFAULTS).
    enableBulkEdit: false,
    // Default ON because the frontend gates the new page on the user's
    // tier as well (only ``subscriptionTier === 'creator'`` sees it).
    // The prod kill switch lives in the backend ``/api/config/feature-flags``
    // response — admins flip it off there if rollout needs to pause.
    enableSubscriptionManagement: true,
    enableCatalogSwitching: true,
    // Default OFF (prod-safe); test env flips it on via the public
    // /api/config/feature-flags response (FEATURE_FLAG_DEFAULTS).
    enableExpiryLifecycle: false,
    // Default OFF (prod-safe); test env flips it on via the public
    // /api/config/feature-flags response (FEATURE_FLAG_DEFAULTS).
    enableCatalogFilters: false,
  };

  constructor() {
    this.loadFromConfig();
  }

  /**
   * Merge flags from `window.featureFlags` (CDK build-time injection).
   * Used as a fallback when the API boot fetch fails.
   */
  private loadFromConfig(): void {
    const windowWithFlags = window as { featureFlags?: Partial<FeatureFlags> };
    if (typeof window !== 'undefined' && windowWithFlags.featureFlags) {
      this.flags = { ...this.flags, ...windowWithFlags.featureFlags };
    }
  }

  /**
   * Fetch the current feature flag values from the backend and merge into
   * state. Call this at app boot in `main.tsx` before React renders so that
   * synchronous `isEnabled()` calls return the runtime values.
   *
   * Contract: this method NEVER throws. On network error, timeout, non-200
   * response, or malformed JSON it logs a warning and leaves the existing
   * flag state untouched — the app must always boot even if the API is down.
   */
  async loadFromApi(apiBaseUrl: string, timeoutMs = 3000): Promise<void> {
    if (!apiBaseUrl) return;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    const trimmedBase = apiBaseUrl.replace(/\/+$/, '');
    const url = `${trimmedBase}${FEATURE_FLAGS_ENDPOINT}`;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) {
        console.warn(
          `[featureFlags] API returned non-OK (${response.status}); keeping existing flags`,
        );
        return;
      }
      const data = (await response.json()) as FeatureFlagsApiResponse;
      if (data && typeof data === 'object' && data.flags && typeof data.flags === 'object') {
        this.flags = { ...this.flags, ...data.flags };
      }
    } catch (error) {
      console.warn('[featureFlags] loadFromApi failed; keeping existing flags', error);
    } finally {
      clearTimeout(timer);
    }
  }

  isEnabled(flag: keyof FeatureFlags): boolean {
    return this.flags[flag];
  }

  updateFlags(newFlags: Partial<FeatureFlags>): void {
    this.flags = { ...this.flags, ...newFlags };
  }

  getAllFlags(): FeatureFlags {
    return { ...this.flags };
  }
}

export const featureFlags = new FeatureFlagService();
